import React, { useCallback, useEffect, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  addAdminEmail,
  deleteTeamImage,
  downloadImageAsBlob,
  getAdminEmails,
  getImage,
  isUat,
  pathForMember,
  pathForMemberOriginal,
  seedUatFromProd,
  signInWithGoogle,
  signOutUser,
  subscribeToAuth,
  updateTeamData,
  uploadTeamImage,
} from "../config/firebaseConfig";
import { getJsonObject } from "../config/config";
import { getCroppedBlob } from "../utils/cropImage";

// ─── Crop Modal ───────────────────────────────────────────────────────────────
// Uses react-easy-crop for professional zoom + pan. Produces a cropped JPEG
// Blob via getCroppedBlob so what you see in the preview is exactly what
// gets stored — no object-position guesswork.

const CROP_ASPECT = 3 / 4; // matches .member-image aspect-ratio: 3/4

function CropModal({ src, cropData, memberName, onConfirm, onClose }) {
  const [crop, setCrop] = useState(cropData?.crop ?? { x: 0, y: 0 });
  const [zoom, setZoom] = useState(cropData?.zoom ?? 1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [baking, setBaking] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!croppedAreaPixels) return;
    setBaking(true);
    try {
      const blob = await getCroppedBlob(src, croppedAreaPixels);
      onConfirm(blob, { crop, zoom });
    } catch (err) {
      toast.error("Crop failed: " + err.message);
    } finally {
      setBaking(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{
        background: "#1e2028", borderRadius: 16, padding: 28,
        display: "flex", flexDirection: "column", gap: 14,
        maxWidth: "95vw", maxHeight: "95vh",
        boxShadow: "0 24px 64px rgba(0,0,0,0.6)",
      }}>
        <div style={{ color: "#aaa", fontSize: 12, textTransform: "uppercase", letterSpacing: "0.08em" }}>
          Drag to reposition · Scroll or slide to zoom · {memberName}
        </div>

        {/* Cropper — container must be position:relative with explicit dimensions */}
        <div style={{
          position: "relative",
          width: 360,
          height: 450,
          borderRadius: 10,
          overflow: "hidden",
          background: "#111",
        }}>
          <Cropper
            image={src}
            crop={crop}
            zoom={zoom}
            aspect={CROP_ASPECT}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{ containerStyle: { borderRadius: 10 } }}
          />
        </div>

        {/* Zoom slider */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ color: "#aaa", fontSize: 13, fontWeight: 700 }}>−</span>
          <input
            type="range" min={1} max={3} step={0.01} value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ flex: 1, accentColor: "#36acfa" }}
          />
          <span style={{ color: "#aaa", fontSize: 13, fontWeight: 700 }}>+</span>
          <span style={{ color: "#555", fontSize: 12, minWidth: 40 }}>
            {Math.round(zoom * 100)}%
          </span>
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <button
            style={{ ...btnStyle, background: "#36acfa", padding: "10px 0", fontSize: 14 }}
            onClick={handleConfirm}
            disabled={baking}
          >
            {baking ? "Processing…" : "Confirm Crop"}
          </button>
          <button
            style={{ ...btnStyle, background: "#374151", padding: "8px 0", fontSize: 13 }}
            onClick={() => { setCrop({ x: 0, y: 0 }); setZoom(1); }}
          >
            Reset
          </button>
          <button
            style={{ ...btnStyle, background: "#6b7280", padding: "8px 0", fontSize: 13 }}
            onClick={onClose}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Single member editor card ────────────────────────────────────────────────

function MemberCard({ member, index, onChange }) {
  const [cropOpen, setCropOpen] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [loadingCropSrc, setLoadingCropSrc] = useState(false);
  // Local blob URL of the baked thumbnail preview (after a crop is confirmed).
  const [localThumbUrl, setLocalThumbUrl] = useState(null);
  const localThumbRef = useRef(null);
  // Tracks the blob URL we created for re-cropping a saved image (needs revocation).
  const fetchedCropBlobRef = useRef(null);
  const fileRef = useRef();

  // Revoke blob URLs on unmount to avoid memory leaks.
  useEffect(() => {
    return () => {
      if (localThumbRef.current) URL.revokeObjectURL(localThumbRef.current);
      if (fetchedCropBlobRef.current) URL.revokeObjectURL(fetchedCropBlobRef.current);
    };
  }, []);

  const update = (field, val) => onChange(index, { ...member, [field]: val });

  // When a new file is selected, store it and immediately open the crop modal.
  const handleFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    const blobUrl = URL.createObjectURL(file);
    // Reset any previous pending state so we start fresh.
    onChange(index, {
      ...member,
      _pendingOriginalFile: file,
      _pendingOriginalExt: ext,
      _pendingCroppedBlob: null,
      cropData: null,
    });
    setCropSrc(blobUrl);
    setCropOpen(true);
    // Reset file input so re-selecting the same file still triggers onChange.
    e.target.value = "";
  };

  // When "Adjust Crop" is clicked, open the modal on the stored original.
  // For images already in Firebase Storage we fetch them as a local blob first.
  // This sidesteps canvas CORS taint — blob URLs are treated as same-origin so
  // canvas.toBlob() never throws a SecurityError regardless of Storage CORS config.
  const handleAdjustCrop = async () => {
    if (member._pendingOriginalFile) {
      // Fresh upload still in memory — blob URL, no CORS concern.
      setCropSrc(URL.createObjectURL(member._pendingOriginalFile));
      setCropOpen(true);
      return;
    }

    // Use the logical storage path (not the download URL) so the Firebase SDK
    // fetches the image internally — no CORS preflight, no browser block.
    const logicalPath = member.originalImage || member.thumbImage;
    if (!logicalPath) {
      toast.error("No source image available for cropping.");
      return;
    }

    setLoadingCropSrc(true);
    try {
      const blob = await downloadImageAsBlob(logicalPath);
      // Revoke any previously fetched blob URL before creating a new one.
      if (fetchedCropBlobRef.current) URL.revokeObjectURL(fetchedCropBlobRef.current);
      const blobUrl = URL.createObjectURL(blob);
      fetchedCropBlobRef.current = blobUrl;
      setCropSrc(blobUrl);
      setCropOpen(true);
    } catch (err) {
      toast.error(
        "Couldn't load the saved image for re-cropping. Use \"Replace Photo\" to " +
          "upload it again, or check the Storage CORS setup. (" + err.message + ")"
      );
    } finally {
      setLoadingCropSrc(false);
    }
  };

  // Called when the user confirms a crop in the modal.
  const handleCropConfirm = (blob, cropData) => {
    // Replace the local preview URL.
    if (localThumbRef.current) URL.revokeObjectURL(localThumbRef.current);
    const thumbUrl = URL.createObjectURL(blob);
    localThumbRef.current = thumbUrl;
    setLocalThumbUrl(thumbUrl);
    onChange(index, { ...member, _pendingCroppedBlob: blob, cropData });
    setCropOpen(false);
  };

  // Display priority: new baked preview → existing thumb from Storage → nothing.
  const displaySrc = localThumbUrl || getImage(member.thumbImage) || "";
  const hasPendingCrop = !!member._pendingCroppedBlob;
  const isDoctor = member.categories?.includes("Doctor");
  const isStudent = member.isStudent;

  return (
    <div style={{
      background: "white", border: "1px solid #e0e0e0",
      borderRadius: 12, padding: 16, display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div>
        <label style={labelStyle}>Name</label>
        <input style={inputStyle} value={member.name || ""}
          onChange={(e) => update("name", e.target.value)} />
      </div>

      <div>
        <label style={labelStyle}>Bio / Info</label>
        <textarea
          style={{ ...inputStyle, height: 80, resize: "vertical" }}
          value={member.projectInfo || ""}
          onChange={(e) => update("projectInfo", e.target.value)}
        />
      </div>

      {isDoctor && (
        <div>
          <label style={labelStyle}>Speciality</label>
          <input style={inputStyle} value={member.speciality || ""}
            onChange={(e) => update("speciality", e.target.value)} />
        </div>
      )}

      {!isDoctor && !isStudent && (
        <div>
          <label style={labelStyle}>Role</label>
          <input style={inputStyle} value={member.role || ""}
            onChange={(e) => update("role", e.target.value)} />
        </div>
      )}

      {isStudent && (
        <>
          <div>
            <label style={labelStyle}>Grade</label>
            <input style={inputStyle} value={member.grade || ""}
              onChange={(e) => update("grade", e.target.value)} />
          </div>
          <div>
            <label style={labelStyle}>Interests</label>
            <input style={inputStyle} value={member.interests || ""}
              onChange={(e) => update("interests", e.target.value)} />
          </div>
        </>
      )}

      <div>
        <label style={labelStyle}>Category</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {["Doctor", "Admin", "Lead", "Student Volunteer"].map((cat) => {
            const active = member.categories?.includes(cat);
            return (
              <button key={cat} type="button"
                onClick={() => {
                  const cats = active
                    ? (member.categories || []).filter((c) => c !== cat)
                    : [...(member.categories || []), cat];
                  update("categories", cats);
                }}
                style={{
                  padding: "3px 10px", borderRadius: 20,
                  border: "1px solid #36acfa",
                  background: active ? "#36acfa" : "white",
                  color: active ? "white" : "#36acfa",
                  cursor: "pointer", fontSize: 12,
                }}
              >
                {cat}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label style={labelStyle}>Photo</label>

        {displaySrc && (
          <div style={{ position: "relative", borderRadius: 8, overflow: "hidden", marginBottom: 8 }}>
            <img
              src={displaySrc}
              alt={member.name}
              style={{
                width: "100%", aspectRatio: "3 / 4",
                objectFit: "cover", objectPosition: "center center",
                display: "block",
              }}
            />
            {/* Overlay button for re-cropping */}
            <button
              type="button"
              onClick={handleAdjustCrop}
              disabled={loadingCropSrc}
              style={{
                position: "absolute", inset: 0, width: "100%", height: "100%",
                background: loadingCropSrc ? "rgba(0,0,0,0.5)" : "rgba(0,0,0,0)",
                border: "none", cursor: loadingCropSrc ? "wait" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                opacity: loadingCropSrc ? 1 : 0, transition: "opacity 0.2s",
              }}
              onMouseEnter={(e) => {
                if (!loadingCropSrc) {
                  e.currentTarget.style.background = "rgba(0,0,0,0.4)";
                  e.currentTarget.style.opacity = "1";
                }
              }}
              onMouseLeave={(e) => {
                if (!loadingCropSrc) {
                  e.currentTarget.style.background = "rgba(0,0,0,0)";
                  e.currentTarget.style.opacity = "0";
                }
              }}
            >
              <span style={{
                color: "white", fontWeight: 700, fontSize: 14,
                background: "rgba(0,0,0,0.6)", padding: "6px 14px", borderRadius: 20,
              }}>
                {loadingCropSrc ? "Loading…" : "✎ Adjust Crop"}
              </span>
            </button>
          </div>
        )}

        {/* Pending-crop status badge */}
        {hasPendingCrop && (
          <div style={{
            fontSize: 11, color: "#059669",
            background: "#d1fae5", padding: "3px 8px", borderRadius: 4,
            marginBottom: 6, display: "inline-block",
          }}>
            ✓ New crop ready — click Save All Changes to apply
          </div>
        )}

        {member.originalImage && !hasPendingCrop && (
          <div style={{ fontSize: 11, color: "#888", marginBottom: 6 }}>
            Original saved · hover photo to re-crop
          </div>
        )}

        <button
          type="button"
          style={{ ...btnStyle, width: "100%" }}
          onClick={() => fileRef.current.click()}
        >
          {displaySrc ? "Replace Photo" : "Upload Photo"}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleFile}
        />
      </div>

      {cropOpen && cropSrc && (
        <CropModal
          src={cropSrc}
          cropData={member.cropData}
          memberName={member.name}
          onConfirm={handleCropConfirm}
          onClose={() => setCropOpen(false)}
        />
      )}
    </div>
  );
}

// ─── Main Admin page ──────────────────────────────────────────────────────────

export default function Admin() {
  const [user, setUser] = useState(undefined);
  const [authorized, setAuthorized] = useState(false);
  const [team, setTeam] = useState([]);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [addingAdmin, setAddingAdmin] = useState(false);
  const [authError, setAuthError] = useState("");

  useEffect(() => {
    const unsub = subscribeToAuth(async (u) => {
      setUser(u);
      if (!u) { setAuthorized(false); return; }
      const emails = await getAdminEmails();
      if (emails.map((e) => e.toLowerCase()).includes(u.email.toLowerCase())) {
        setAuthorized(true); setAuthError("");
      } else {
        setAuthError(`${u.email} is not authorized. Contact an admin.`);
        await signOutUser(); setAuthorized(false);
      }
    });
    return unsub;
  }, []);

  useEffect(() => {
    if (!authorized) return;
    try { setTeam(getJsonObject("team") || []); } catch { setTeam([]); }
  }, [authorized]);

  const handleChange = useCallback((index, updated) => {
    setTeam((prev) => prev.map((m, i) => (i === index ? updated : m)));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const toSave = await Promise.all(
        team.map(async (m) => {
          const member = { ...m };

          // 1. Upload the full-resolution original (only if a new file was chosen).
          if (member._pendingOriginalFile) {
            const ext = member._pendingOriginalExt || "jpg";
            const origPath = pathForMemberOriginal(member.name, member.categories || [], ext);
            // Delete the old original if its path changed (e.g. name/category edit).
            if (member.originalImage && member.originalImage !== origPath) {
              await deleteTeamImage(member.originalImage);
            }
            await uploadTeamImage(member._pendingOriginalFile, origPath);
            member.originalImage = origPath;
          }

          // 2. Upload the baked JPEG crop as the display thumbnail.
          if (member._pendingCroppedBlob) {
            const thumbPath = pathForMember(member.name, member.categories || []);
            // Delete the old thumb if its path changed.
            if (member.thumbImage && member.thumbImage !== thumbPath) {
              await deleteTeamImage(member.thumbImage);
            }
            await uploadTeamImage(member._pendingCroppedBlob, thumbPath);
            member.thumbImage = thumbPath;
            // Image is already cropped; center positioning is correct for any container.
            member.imagePosition = "50% 50%";
          }

          // Strip transient fields before writing to Firestore.
          delete member._pendingOriginalFile;
          delete member._pendingOriginalExt;
          delete member._pendingCroppedBlob;

          return member;
        })
      );

      // Single Firestore write for the whole team.
      await updateTeamData(toSave);
      setTeam(toSave);
      toast.success("Team saved successfully!");
    } catch (err) {
      toast.error("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("This will overwrite InfoTest / UrlsTest with prod data and copy all Storage images to uat/. Continue?")) return;
    setSeeding(true);
    try {
      const { total, copied } = await seedUatFromProd();
      setTeam(getJsonObject("team") || []);
      if (copied === total) {
        toast.success(`UAT fully synced — ${copied} image${copied !== 1 ? "s" : ""} copied to UAT storage.`);
      } else {
        toast.warn(
          `Firestore synced. Storage: ${copied}/${total} images copied` +
          (copied < total ? " (remaining will display via prod URLs — apply CORS to copy all)." : ".")
        );
      }
    } catch (err) {
      toast.error("Seed failed: " + err.message);
    } finally {
      setSeeding(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail.trim()) return;
    setAddingAdmin(true);
    try {
      await addAdminEmail(newAdminEmail.trim());
      toast.success(`${newAdminEmail.trim()} authorized!`);
      setNewAdminEmail("");
    } catch (err) {
      toast.error("Failed: " + err.message);
    } finally {
      setAddingAdmin(false);
    }
  };

  if (user === undefined) return <CenteredMsg>Loading…</CenteredMsg>;

  if (!user || !authorized) {
    return (
      <div style={pageStyle}>
        <div style={loginBoxStyle}>
          <h2 style={{ marginBottom: 8 }}>GHMC Admin</h2>
          <p style={{ color: "#888", marginBottom: 20, fontSize: 14 }}>
            Sign in with an authorized Google account
          </p>
          {authError && (
            <div style={{ color: "red", marginBottom: 12, fontSize: 13 }}>{authError}</div>
          )}
          <button
            style={{ ...btnStyle, padding: "10px 24px", fontSize: 15 }}
            onClick={async () => {
              setAuthError("");
              try { await signInWithGoogle(); }
              catch (e) { setAuthError("Sign-in failed: " + e.message); }
            }}
          >
            <i className="fab fa-google" style={{ marginRight: 8 }} />
            Sign in with Google
          </button>
        </div>
        <ToastContainer position="bottom-right" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f5f7fa", padding: 24 }}>
      <ToastContainer position="bottom-right" />

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24 }}>GHMC Admin</h1>
          <div style={{ color: "#888", fontSize: 13 }}>Signed in as {user.email}</div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {isUat() && (
            <button
              style={{ ...btnStyle, background: "#f59e0b", fontSize: 13 }}
              onClick={handleSeed}
              disabled={seeding}
            >
              {seeding ? "Seeding…" : "Seed UAT from Prod"}
            </button>
          )}
          <button
            style={{ ...btnStyle, background: "#6b7280", fontSize: 13 }}
            onClick={signOutUser}
          >
            Sign Out
          </button>
        </div>
      </div>

      {/* UAT banner */}
      {isUat() && (
        <div style={{
          background: "#fef3c7", border: "1px solid #f59e0b",
          borderRadius: 8, padding: "10px 16px", marginBottom: 20,
          fontSize: 13, color: "#92400e",
        }}>
          <strong>UAT mode</strong> — all saves go to <code>InfoTest</code> / <code>UrlsTest</code>.
          Production data is never modified.
        </div>
      )}

      {/* Authorize admin */}
      <div style={{
        background: "white", borderRadius: 12, padding: 16,
        marginBottom: 24, border: "1px solid #e0e0e0",
      }}>
        <h3 style={{ marginTop: 0, marginBottom: 12, fontSize: 16 }}>Authorize a Google Account</h3>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            style={{ ...inputStyle, flex: 1 }}
            type="email"
            placeholder="name@gmail.com"
            value={newAdminEmail}
            onChange={(e) => setNewAdminEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleAddAdmin()}
          />
          <button style={btnStyle} onClick={handleAddAdmin} disabled={addingAdmin}>
            {addingAdmin ? "Adding…" : "Authorize"}
          </button>
        </div>
      </div>

      {/* Team grid */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Team Members ({team.length})</h2>
        <button
          style={{ ...btnStyle, padding: "10px 24px" }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
        gap: 16,
      }}>
        {team.map((member, i) => (
          <MemberCard key={i} member={member} index={i} onChange={handleChange} />
        ))}
      </div>

      <div style={{ textAlign: "right", marginTop: 24 }}>
        <button
          style={{ ...btnStyle, padding: "10px 24px" }}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save All Changes"}
        </button>
      </div>
    </div>
  );
}

// ─── Small helpers ────────────────────────────────────────────────────────────

function CenteredMsg({ children }) {
  return (
    <div style={{ display: "flex", height: "100vh", alignItems: "center", justifyContent: "center" }}>
      {children}
    </div>
  );
}

const pageStyle = {
  display: "flex", height: "100vh",
  alignItems: "center", justifyContent: "center",
  background: "#f5f7fa",
};
const loginBoxStyle = {
  background: "white", borderRadius: 16, padding: 40,
  textAlign: "center", boxShadow: "0 4px 24px rgba(0,0,0,0.08)", minWidth: 320,
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "8px 10px", border: "1px solid #d1d5db",
  borderRadius: 6, fontSize: 14,
};
const btnStyle = {
  padding: "8px 16px", background: "#36acfa",
  color: "white", border: "none", borderRadius: 6,
  cursor: "pointer", fontWeight: 600,
};
const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600,
  color: "#4b5563", marginBottom: 4,
  textTransform: "uppercase", letterSpacing: "0.05em",
};
